/**
 * OrderTableCell Component
 * Renders individual cells in the order details table with conditional styling
 */

import React, { useMemo } from 'react';
import type { OrderTableColumn } from '../../types/components';
import type { OrderSummary } from '../../utils/types';
import { applyCellStyles, shouldHideCell, formatCellValue } from '../../utils/orderTableHelpers';
import { GlassValueDisplay } from './GlassValueDisplay';
import { ExpandableText } from './ExpandableText';
import { useGetGlassValuesDropdownQuery } from '../../store/slices/apiSlice';
import {
  formatGlassSideDisplay,
  glassSideFromDisplayColumnKey,
} from '../../utils/formatGlassSideDisplay';

interface OrderTableCellProps {
  value: unknown;
  order: OrderSummary;
  column: OrderTableColumn;
  className?: string;
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>;
  stickyStyle?: React.CSSProperties;
}

export const OrderTableCell: React.FC<OrderTableCellProps> = React.memo(({
  value,
  order,
  column,
  className = '',
  glassValuesLookup: providedLookup,
  stickyStyle = {}
}) => {
  // Fetch glass values dropdown data for lookup (if not provided)
  const { data: glassValuesData } = useGetGlassValuesDropdownQuery({});
  
  // Create lookup map from glass values data
  const glassValuesLookup = useMemo(() => {
    // Use provided lookup if available
    if (providedLookup) {
      return providedLookup;
    }
    
    // Otherwise, create lookup from fetched data
    if (glassValuesData?.results) {
      const lookup = new Map<string, { is_arrival: boolean; is_cutting: boolean }>();
      glassValuesData.results.forEach((gv) => {
        lookup.set(gv.value, { is_arrival: gv.is_arrival, is_cutting: gv.is_cutting });
      });
      return lookup;
    }
    
    return undefined;
  }, [providedLookup, glassValuesData]);

  const glassLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    glassValuesData?.results?.forEach((gv) => {
      map[gv.value] = gv.label;
    });
    return map;
  }, [glassValuesData]);

  // Check if cell should be hidden based on conditional logic
  if (shouldHideCell(value, order, column)) {
    return null;
  }

  // Apply conditional styles with glass values lookup
  const { className: conditionalClass, style } = applyCellStyles(value, order, column, glassValuesLookup);

  // Handle special component cases
  let displayValue: React.ReactNode;
  
  const meta = order.metadata;
  const wooFromMeta = meta?.woo_glass_raw;
  const wooGlass =
    order.woo_glass_raw ??
    (wooFromMeta && typeof wooFromMeta === 'object'
      ? (wooFromMeta as { side_one?: string; side_two?: string; side_three?: string })
      : undefined);
  const md = meta ?? {};
  const isGlassValueColumn =
    column.key.includes('value_id') &&
    (column.key.includes('glass_side') || column.key.includes('middle_glass_panel'));
  if (isGlassValueColumn) {
    // Render GlassValueDisplay for glass value columns; fallback to woo_glass_raw when no match
    const rawFallback =
      column.key.includes('one') ? wooGlass?.side_one :
      column.key.includes('two') ? wooGlass?.side_two :
      column.key.includes('three') || column.key.includes('middle') ? wooGlass?.side_three :
      undefined;
    const patternId = (k: string) => {
      const v = md[k];
      return typeof v === 'string' && v ? v : undefined;
    };
    const wooPatterned = md.woo_patterned_glass && typeof md.woo_patterned_glass === 'object'
      ? (md.woo_patterned_glass as { one?: string; two?: string; three?: string })
      : undefined;
    let patternedOptionId: string | undefined;
    let patternedRawFallback: string | undefined;
    if (column.key.includes('glass_side_one')) patternedOptionId = patternId('patterned_gs_one_option_id');
    else if (column.key.includes('glass_side_two')) patternedOptionId = patternId('patterned_gs_two_option_id');
    else if (column.key.includes('middle_glass_panel') || column.key.includes('glass_side_three')) {
      patternedOptionId = patternId('patterned_gs_three_option_id');
    }
    if (column.key.includes('glass_side_one')) patternedRawFallback = wooPatterned?.one;
    else if (column.key.includes('glass_side_two')) patternedRawFallback = wooPatterned?.two;
    else if (column.key.includes('middle_glass_panel') || column.key.includes('glass_side_three')) {
      patternedRawFallback = wooPatterned?.three;
    }
    displayValue = (
      <GlassValueDisplay
        glassValueId={typeof value === 'string' ? value : null}
        rawFallback={rawFallback}
        patternedOptionId={patternedOptionId}
        patternedRawFallback={patternedRawFallback}
      />
    );
  } else if (
    column.key === 'glass_side_one_display' ||
    column.key === 'glass_side_two_display' ||
    column.key === 'middle_glass_panel_display'
  ) {
    const side = glassSideFromDisplayColumnKey(column.key);
    const formatted =
      side != null
        ? formatGlassSideDisplay(order, side, { glassLabelMap, preferApiDisplay: true })
        : '';
    displayValue = formatted || formatCellValue(value, column, order);
  } else if (
    column.key === 'images_available' ||
    column.key === 'image_types' ||
    column.key === 'image_actions'
  ) {
    // Populated by OrderImagesCells in OrderTableRow; avoid misleading '-' from nested path
    displayValue = null;
  } else if (
    column.type === 'text' &&
    column.textDisplay?.expandable &&
    !column.render
  ) {
    displayValue = (
      <ExpandableText
        text={value != null ? String(value) : ''}
        collapsedLines={column.textDisplay.collapsedLines ?? 3}
        buttonClassName="text-blue-600 hover:text-blue-800 text-xs font-medium mt-0.5"
      />
    );
  } else {
    // Standard formatting
    displayValue = formatCellValue(value, column, order);
  }

  // Combine CSS classes
  const cellClasses = [
    'p-2 border',
    column.align === 'center' && 'text-center',
    column.align === 'right' && 'text-right',
    conditionalClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <td 
      className={cellClasses}
      style={{ ...style, ...stickyStyle }}
    >
      {displayValue}
    </td>
  );
});

OrderTableCell.displayName = 'OrderTableCell';
